import React, { useEffect, useMemo, useState } from 'react';
import { createSuggestion, getMySuggestions, customerConfirmSuggestion, deleteSuggestion, restoreSuggestion } from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Lightbulb, CheckCircle2, Clock, XCircle, FlaskConical, Code2, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  pending:       { label: 'Awaiting review',     tone: 'bg-slate-100 text-slate-700 border-slate-200',     Icon: Clock },
  approved:      { label: 'Approved',            tone: 'bg-blue-50 text-blue-700 border-blue-200',         Icon: CheckCircle2 },
  in_dev:        { label: 'In development',      tone: 'bg-indigo-50 text-indigo-700 border-indigo-200',   Icon: Code2 },
  admin_test:    { label: 'Lutrom is testing',   tone: 'bg-amber-50 text-amber-800 border-amber-200',      Icon: FlaskConical },
  customer_test: { label: 'Ready for you',       tone: 'bg-teal-50 text-teal-800 border-teal-200',         Icon: Sparkles },
  done:          { label: 'Live',                tone: 'bg-green-50 text-green-700 border-green-200',      Icon: CheckCircle2 },
  rejected:      { label: 'Not pursued',         tone: 'bg-rose-50 text-rose-700 border-rose-200',         Icon: XCircle },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-full ${meta.tone}`}>
      <Icon className="h-3.5 w-3.5" />
      {meta.label}
    </span>
  );
};

const CustomerSuggestions = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', area: 'general' });
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(null);

  const refresh = () => getMySuggestions().then(setItems);
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const submit = async () => {
    if (!form.title.trim()) return toast.error('Give your idea a title');
    setSubmitting(true);
    try {
      await createSuggestion({ ...form, title: form.title.trim(), description: form.description.trim() });
      toast.success('Thanks! Your suggestion has been sent to Lutrom for review.');
      setOpen(false);
      setForm({ title: '', description: '', area: 'general' });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDone = async (s) => {
    setConfirming(s.id);
    try {
      await customerConfirmSuggestion(s.id);
      toast.success('Thanks for confirming!');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not confirm');
    } finally {
      setConfirming(null);
    }
  };

  // Customers can only retract their own suggestion while it's still in
  // `pending`. Once Lutrom starts working on it, the audit trail stands.
  const removeMine = async (s) => {
    try {
      await deleteSuggestion(s.id);
      const snapshot = items;
      setItems((list) => list.filter(x => x.id !== s.id));
      toast.success(`Suggestion "${s.title}" withdrawn`, {
        duration: 6000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await restoreSuggestion(s.id);
              setItems(snapshot);
              toast.success('Restored');
            } catch {
              toast.error('Restore failed');
            }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not withdraw');
    }
  };

  const grouped = useMemo(() => {
    const open = items.filter(s => !['done', 'rejected'].includes(s.status));
    const closed = items.filter(s => ['done', 'rejected'].includes(s.status));
    return { open, closed };
  }, [items]);

  if (loading) return <div className="p-8 text-slate-500">Loading suggestions...</div>;

  const renderCard = (s) => {
    const meta = STATUS_META[s.status] || STATUS_META.pending;
    return (
      <div key={s.id} data-testid={`suggestion-card-${s.id}`} className="bg-white border border-slate-200 rounded-md p-5 hover:border-slate-300 transition-colors">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-mono text-slate-500">{s.id}</div>
            <h3 className="font-display uppercase text-base text-slate-900 tracking-wide mt-0.5">{s.title}</h3>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <StatusPill status={s.status} />
            {s.status === 'pending' && (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                onClick={() => removeMine(s)}
                title="Withdraw suggestion"
                data-testid={`withdraw-suggestion-${s.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
        {s.description && (
          <p className="text-sm text-slate-600 whitespace-pre-wrap mb-3">{s.description}</p>
        )}
        <div className="text-[11px] uppercase tracking-wider text-slate-400">
          {s.area || 'general'}
        </div>

        {s.status === 'customer_test' && (
          <div className="mt-4 p-3 bg-teal-50 border border-teal-200 rounded text-sm text-teal-900 flex items-center justify-between gap-3">
            <span>Lutrom has built and tested your suggestion. Try it and let us know it works.</span>
            <Button
              size="sm"
              onClick={() => confirmDone(s)}
              disabled={confirming === s.id}
              className="shrink-0 bg-teal-700 hover:bg-teal-800"
              data-testid={`confirm-suggestion-${s.id}`}
            >
              {confirming === s.id ? 'Saving...' : 'Confirm it works'}
            </Button>
          </div>
        )}

        {s.status === 'rejected' && s.history?.length > 0 && (
          <div className="mt-3 text-xs text-rose-700">
            {s.history[s.history.length - 1]?.note || 'Closed without action'}
          </div>
        )}

        {/* History footer */}
        {s.history?.length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
            Last update {new Date(s.history[s.history.length - 1].at).toLocaleDateString()}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Suggestions"
        description="Got an idea to make this portal better? Tell us — we triage every suggestion."
        actions={
          <Button onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="new-suggestion-btn">
            <Lightbulb className="h-4 w-4 mr-2" /> New suggestion
          </Button>
        }
      />

      {items.length === 0 && (
        <div className="p-12 text-center text-slate-500 bg-white border border-dashed border-slate-200 rounded-md">
          <Lightbulb className="h-10 w-10 mx-auto mb-3 text-slate-400" />
          <div className="font-medium text-slate-700 mb-1">No suggestions yet</div>
          <div className="text-sm">Click <strong>New suggestion</strong> to share your first idea.</div>
        </div>
      )}

      {grouped.open.length > 0 && (
        <section>
          <h2 className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">In progress</h2>
          <div className="grid md:grid-cols-2 gap-4">{grouped.open.map(renderCard)}</div>
        </section>
      )}

      {grouped.closed.length > 0 && (
        <section>
          <h2 className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Closed</h2>
          <div className="grid md:grid-cols-2 gap-4">{grouped.closed.map(renderCard)}</div>
        </section>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suggest an improvement</DialogTitle>
            <DialogDescription>
              Tell us what would make this portal better for your team. We review every one and keep you posted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Idea *</Label>
              <Input
                className="mt-1.5"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Show last 12 months of invoices"
                data-testid="suggestion-title-input"
              />
            </div>
            <div>
              <Label>Why is it useful?</Label>
              <Textarea
                className="mt-1.5"
                rows={5}
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="What problem does it solve? Who benefits? Any examples?"
                data-testid="suggestion-description-input"
              />
            </div>
            <div>
              <Label>Where in the app?</Label>
              <Input
                className="mt-1.5"
                value={form.area}
                onChange={e => setForm({ ...form, area: e.target.value })}
                placeholder="e.g. invoices, mobile, parts pickup"
                data-testid="suggestion-area-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={submit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800" data-testid="submit-suggestion-btn">
              {submitting ? 'Submitting...' : 'Send to Lutrom'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerSuggestions;
